import React, { Suspense, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  AppBar,
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
  Button,
  Menu,
  MenuItem,
  Avatar,
  Stack,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Source as DataSourcesIcon,
  AccountTree as PipelinesIcon,
  Hub as EntityModelsIcon,
  Timeline as LineageIcon,
  Settings as SettingsIcon,
  Public as GisIcon,
  HelpOutline as SupportIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Storage as SqlTrinoIcon,
  AutoFixHigh as TransformIcon,
  DashboardCustomize as DashboardsIcon,
  Assessment as ReportsIcon,
  Groups as GroupsIcon,
  AssignmentTurnedIn as ActionsIcon,
  Bolt as RulesIcon,
  SendOutlined as ChannelsIcon,
} from '@mui/icons-material';
import {
  getAddonNavIcon,
  getAddonRoutePath,
  hasCompiledMainNavForExtension,
} from '../../addons/compiledAddonRegistry';
import { useAuth } from '../../hooks/useAuth';
import { signInMethodMenuItems } from '../auth/signInMethodMenuItems';
import SignInActionNotice from '../auth/SignInActionNotice';
import type { ActionName } from '../../utils/actionReturn';
import { useTheme } from '../../hooks/useTheme';
import { useFeatures } from '../../hooks/useFeatures';
import { useAddons } from '../../hooks/useAddons';
import { canAdminActions, canAdminRules, canAdminChannels, canUseTransformations, isViewerOnly } from '../../utils/capabilities';
import ErrorBoundary from '../ErrorBoundary';
import NotificationCentre from '../actions/NotificationCentre';
import RulesBanner from '../actions/RulesBanner';

const drawerWidth = 240;

// Navigation sections. The customer app is a RISK console first: the `primary`
// section leads with risk-domain surfaces; `tools` groups the analysis/plumbing
// surfaces (lineage, workbench, pipelines, SQL, entity models) plus addon nav
// items; `system` holds support/settings. Sections render in this order.
type NavSection = 'primary' | 'tools' | 'system';

const NAV_SECTION_ORDER: NavSection[] = ['primary', 'tools', 'system'];

// Only named sections render a subheader; the primary/system groups are unlabelled.
const NAV_SECTION_LABELS: Record<NavSection, string | null> = {
  primary: null,
  tools: 'Analysis & Tools',
  system: null,
};

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  section: NavSection;
  order: number;
  requiresFeature?: 'trino' | 'dashboards' | 'gis' | 'entityModelsUi' | 'actions' | 'rules' | 'channels';
  editorOnly?: boolean;
  actionAdminOnly?: boolean;
  ruleAdminOnly?: boolean;
  channelAdminOnly?: boolean;
  transformationsOnly?: boolean;
}

// Menu items configuration - filtering happens inside the component based on dynamic
// feature flags and the user's role capabilities. `editorOnly` items are edit
// affordances hidden from read-only (viewer) users so they stop hitting 403s. Every
// relocated item keeps the exact `editorOnly` / `requiresFeature` semantics it had
// before the risk-first IA restructure, so viewer gating and route guards are unchanged.
const allMenuItems: NavItem[] = [
  { text: 'Datasets', icon: <DashboardIcon />, path: '/', section: 'primary', order: 0 },
  { text: 'Risk Dashboards', icon: <DashboardsIcon />, path: '/dashboards', requiresFeature: 'dashboards', section: 'primary', order: 10 },
  // Reports: viewer-safe, print-friendly catalog of the tenant's dashboards. Shares the
  // dashboards feature flag (no dashboards => no report data) but is NOT editorOnly, so
  // read-only viewers keep it.
  { text: 'Reports', icon: <ReportsIcon />, path: '/reports', requiresFeature: 'dashboards', section: 'primary', order: 15 },
  { text: 'Actions', icon: <ActionsIcon />, path: '/actions', requiresFeature: 'actions', section: 'primary', order: 17 },
  { text: 'Action Groups', icon: <GroupsIcon />, path: '/action-groups', requiresFeature: 'actions', actionAdminOnly: true, section: 'tools', order: 85 },
  { text: 'Rules', icon: <RulesIcon />, path: '/rules', requiresFeature: 'rules', ruleAdminOnly: true, section: 'tools', order: 86 },
  { text: 'Outbound Channels', icon: <ChannelsIcon />, path: '/channels', requiresFeature: 'channels', channelAdminOnly: true, section: 'tools', order: 87 },
  { text: 'Feeds & Sources', icon: <DataSourcesIcon />, path: '/data-sources', editorOnly: true, section: 'primary', order: 20 },
  { text: 'Map', icon: <GisIcon />, path: '/gis', requiresFeature: 'gis', section: 'primary', order: 30 },
  // Geofence zone authoring — Phase 3 Rules feature (ADR-022 §14), editor-only. Gated on the
  // rules feature (reconciled at Wave-3 integration; supersedes the standalone gis gate).
  { text: 'Zones', icon: <GisIcon />, path: '/zones', requiresFeature: 'rules', editorOnly: true, section: 'primary', order: 32 },
  { text: 'Data Lineage', icon: <LineageIcon />, path: '/lineage', section: 'tools', order: 40 },
  { text: 'Transformation Workbench', icon: <TransformIcon />, path: '/transformations', editorOnly: true, transformationsOnly: true, section: 'tools', order: 50 },
  { text: 'Pipelines', icon: <PipelinesIcon />, path: '/pipelines', editorOnly: true, section: 'tools', order: 60 },
  { text: 'SQL (Trino)', icon: <SqlTrinoIcon />, path: '/sql/trino', requiresFeature: 'trino', section: 'tools', order: 70 },
  { text: 'Entity Models', icon: <EntityModelsIcon />, path: '/entity-models', requiresFeature: 'entityModelsUi', editorOnly: true, section: 'tools', order: 80 },
  { text: 'User Groups', icon: <GroupsIcon />, path: '/groups', section: 'tools', order: 90 },
  { text: 'Support', icon: <SupportIcon />, path: '/support', section: 'system', order: 900 },
  { text: 'Settings', icon: <SettingsIcon />, path: '/settings', section: 'system', order: 910 },
];

const MainLayout: React.FC = () => {
  // Get feature flags from backend (dynamically fetched at app startup)
  const { actionsEnabled, rulesEnabled, channelsEnabled, trinoEnabled, dashboardsEnabled, gisEnabled, entityModelsUiEnabled } = useFeatures();
  const { mainNavExtensions } = useAddons();
  const { user, logout, login, addSignInMethod } = useAuth();
  const viewerOnly = isViewerOnly(user?.roles);

  // Filter menu items based on feature flags and role capabilities
  const menuItems = useMemo(
    () => {
      const staticItems = allMenuItems.filter(item => {
        if (item.editorOnly && viewerOnly) {
          return false;
        }
        if (item.actionAdminOnly && !canAdminActions(user?.roles)) {
          return false;
        }
        if (item.ruleAdminOnly && !canAdminRules(user?.roles)) {
          return false;
        }
        if (item.channelAdminOnly && !canAdminChannels(user?.roles)) {
          return false;
        }
        if (item.transformationsOnly && !canUseTransformations(user?.roles)) {
          return false;
        }
        switch (item.requiresFeature) {
          case 'trino':
            return trinoEnabled;
          case 'dashboards':
            return dashboardsEnabled;
          case 'gis':
            return gisEnabled;
          case 'entityModelsUi':
            return entityModelsUiEnabled;
          case 'actions':
            return actionsEnabled;
          case 'rules':
            return rulesEnabled;
          case 'channels':
            return channelsEnabled;
          default:
            return true;
        }
      });
      const addonItems = mainNavExtensions
        .filter(hasCompiledMainNavForExtension)
        .map(extension => {
          const icon = getAddonNavIcon(extension.manifest.icon);
          const path = getAddonRoutePath(extension);
          if (!icon || !path) {
            return null;
          }
          // Addon nav items are extension surfaces -> grouped under Analysis & Tools.
          return {
            text: extension.manifest.label || extension.extensionKey,
            icon,
            path,
            section: 'tools' as NavSection,
            order: extension.manifest.order ?? 500,
          };
        })
        .filter((item): item is NavItem => Boolean(item));
      return [...staticItems, ...addonItems].sort((left, right) => left.order - right.order);
    },
    [actionsEnabled, rulesEnabled, channelsEnabled, dashboardsEnabled, entityModelsUiEnabled, gisEnabled, mainNavExtensions, trinoEnabled, user?.roles, viewerOnly]
  );
  const { mode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const tenantMissing = !user?.tenantId;
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };
  
  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  const handleAddSignInMethod = (action: ActionName) => {
    handleUserMenuClose();
    addSignInMethod(action);
  };

  const handleReauthenticate = () => {
    login();
  };

  const handleSupport = () => {
    navigate('/support?topic=tenant-access');
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleMenuReauthenticate = () => {
    handleUserMenuClose();
    login();
  };

  const currentPageTitle =
    menuItems.find(
      item => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
    )?.text || 'Stratforth';

  const renderNavItem = (item: NavItem) => (
    <ListItem key={item.text} disablePadding>
      <ListItemButton
        selected={location.pathname === item.path}
        onClick={() => {
          navigate(item.path);
          if (isMobile) {
            setMobileOpen(false);
          }
        }}
      >
        <ListItemIcon>
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={item.text} />
      </ListItemButton>
    </ListItem>
  );

  // The drawer content, grouped into risk-first sections. Each section renders only
  // if it has visible items after feature-flag and viewer-gating filtering.
  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Stratforth
        </Typography>
      </Toolbar>
      <Divider />
      {NAV_SECTION_ORDER.map((section, sectionIndex) => {
        const sectionItems = menuItems.filter(item => item.section === section);
        if (sectionItems.length === 0) {
          return null;
        }
        const label = NAV_SECTION_LABELS[section];
        return (
          <React.Fragment key={section}>
            {sectionIndex > 0 && <Divider />}
            <List
              subheader={
                label ? (
                  <ListSubheader component="div" disableSticky>
                    {label}
                  </ListSubheader>
                ) : undefined
              }
            >
              {sectionItems.map(renderNavItem)}
            </List>
          </React.Fragment>
        );
      })}
    </div>
  );
  
  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* Top app bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {currentPageTitle}
          </Typography>

          {actionsEnabled && <NotificationCentre />}
          
          {/* Theme toggle button */}
          <IconButton color="inherit" onClick={toggleTheme} sx={{ mr: 2 }}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
          
          {/* User menu */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              color="inherit"
              // The visible name starts the accessible one (WCAG 2.5.3); at xs only the avatar shows.
              aria-label={`${user?.username || 'User'}, account menu`}
              onClick={handleUserMenuOpen}
              endIcon={<ArrowDropDownIcon />}
              sx={{ '& .MuiButton-startIcon': { flexShrink: 0 } }}
              startIcon={
                <Avatar 
                  sx={{ width: 32, height: 32 }}
                  alt={user?.username || 'User'}
                  src="/static/images/avatar/default.jpg"
                />
              }
            >
              {!isMobile && user?.username}
            </Button>
            <Menu
              anchorEl={userMenuAnchorEl}
              open={Boolean(userMenuAnchorEl)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {/* The full name, which the trigger hides on small screens (UX-07). Not focusable. */}
              <ListSubheader disableSticky sx={{ lineHeight: 1.5, py: 1, color: 'text.primary', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                {user?.username || 'User'}
              </ListSubheader>
              <MenuItem disabled>
                <Typography variant="body2" color="textSecondary">
                  Tenant: {user?.tenantId ?? 'Not detected'}
                </Typography>
              </MenuItem>
              {tenantMissing && (
                <MenuItem onClick={handleMenuReauthenticate}>
                  <Typography variant="body2" sx={{ color: 'warning.main' }}>
                    Re-authenticate to restore tenant access
                  </Typography>
                </MenuItem>
              )}
              <Divider />
              {signInMethodMenuItems(handleAddSignInMethod)}
              <Divider />
              <MenuItem onClick={handleLogout}>Sign out</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: ['56px', '64px'],
        }}
      >
        <SignInActionNotice />
        {tenantMissing && (
          <Box mb={2}>
            <Alert severity="warning">
              <AlertTitle>Tenant context unavailable</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Your current session no longer includes a tenant. Re-authenticate or sign out to refresh your
                workspace access.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={handleReauthenticate}
                >
                  Re-authenticate
                </Button>
                <Button variant="outlined" color="warning" onClick={handleLogout}>
                  Sign out
                </Button>
                <Button color="warning" onClick={handleSupport}>
                  Contact support
                </Button>
              </Stack>
            </Alert>
          </Box>
        )}
        {/* Persistent tenant-wide rule health banner (ADR-022 §9); self-gates behind rulesEnabled. */}
        <RulesBanner />
        <ErrorBoundary resetKey={location.pathname} title={currentPageTitle}>
          {/* Suspense keeps the nav shell mounted while a lazy route chunk loads. */}
          <Suspense
            fallback={
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                <CircularProgress />
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </Box>
    </Box>
  );
};

export default MainLayout;
